import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import BaseUrl from "../../Components/BaseUrl";
import Cookies from "js-cookie";
import DeleteIcon from '@mui/icons-material/Delete';
import { FiSearch } from "react-icons/fi";
import { BsThreeDots } from "react-icons/bs";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

const ConnectionsList = () => {
  const [isloading, setIsLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [allConnections, setAllConnections] = useState([]); 
  const [filteredConnections, setFilteredConnections] = useState([]);
  const [page, setPage] = useState(1);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [sortOption, setSortOption] = useState("recent");
  const [confirmDelete, setConfirmDelete] = useState({
    show: false,
    id: null,
    name: "",
  });
  const [searchTerm, setSearchTerm] = useState(""); 
  const [meta, setMeta] = useState(null); 

  const handleDotsClick = (index) => {
    setOpenDropdownIndex((prev) => (prev === index ? null : index));
  };

  const handleConfirmDelete = (id, name) => {
    setConfirmDelete({ show: true, id, name });
    setOpenDropdownIndex(null); 
  };
  const getColorFromName = (name) => {
    if (!name) return { backgroundColor: "hsl(0, 0%, 50%)" };

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash) % 360;
    const saturation = 70 + (Math.abs(hash) % 30); // 70–99%
    const lightness = 50 + (Math.abs(hash) % 10);

    return {
      backgroundColor: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
    };
  };

  const handleDelete = async(id) => {
    const token = Cookies.get("token");
    const formData = new FormData();
    formData.append("id", id);
    try {
      const result = await axios.post(BaseUrl + `rejectrequest`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      if (result.data.status === 200) {
        fetchConnections();
        setConfirmDelete({ show: false, id: null, name: "" });
      }
    } catch (error) {
      console.log(error);
    }
    setConfirmDelete({ show: false, id: null, name: "" });
  };

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    if (term === "") {
      setFilteredConnections(allConnections);
      setCount(allConnections.length);
    } else {
      const filtered = allConnections.filter(conn => 
        conn.name.toLowerCase().includes(term.toLowerCase())
      );
      setFilteredConnections(filtered);
      setCount(filtered.length);
    }
  };

  const dropdownRef = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchConnections = async (pageNumber = 1) => {
    setIsLoading(true);
    try {
      const token = Cookies.get("token");
      const response = await axios.get(
        BaseUrl + `connections?page=${pageNumber}&per_page=10`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.status === 200) {
        setAllConnections(response.data.data);
        setFilteredConnections(response.data.data);
        setCount(response.data.data.length);
        setMeta(response.data.meta); // Assuming your API returns pagination meta data
      }
    } catch (error) {
      console.error("Error fetching connections:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConnections(page);
  }, [page]);

  const handleNext = () => {
    if (meta && page < meta.totalPages) {
      setPage((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (meta && page > 1) {
      setPage((prev) => prev - 1);
    }
  };

  const sortConnections = (connections, option) => {
    const sorted = [...connections];
    
    switch (option) {
      case "recent":
        return sorted.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
      case "name_asc":
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case "name_desc":
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
      default:
        return sorted;
    }
  };

  return (
    <>
      {isloading ? (
        <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-10">
          <img src="/Images/loader.gif" className="h-16 w-16 bg-opacity-50" />
        </div>
      ) : (
        <div className="bg-[#F3F2EF] mt-10 ">
          <div className="container mx-auto flex flex-col lg:flex-row py-8 gap-4 px-4 sm:px-6 lg:px-8">
            <div className="w-full lg:w-3/4 ">
              <div className=" bg-white border border-gray-200 rounded-md shadow  ">
                <div className="flex justify-between items-center px-4 py-2">
                  <h2 className="text-lg font-semibold">{count} Connections</h2>
                </div>
                <div className="flex items-center justify-between border-b px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Sort by:</span>
                    <select
                      className="text-sm border rounded px-2 py-1"
                      value={sortOption}
                      onChange={(e) => setSortOption(e.target.value)}
                    >
                      <option value="recent">Recently added</option>
                      <option value="name_asc">By Name (Ascending)</option>
                      <option value="name_desc">By Name (Descending)</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative w-72">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FiSearch className="text-gray-400 text-lg" />
                      </span>
                      <input
                        type="text"
                        placeholder="Search by name"
                        className="border w-full rounded px-9 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-black"
                        value={searchTerm}
                        onChange={handleSearchChange}
                      />
                    </div>

                    <button className="text-blue-600 text-sm">
                      Search with filters
                    </button>
                  </div>
                </div>

                <div className="space-y-4  py-4">
                  {filteredConnections.length > 0 ? (
                    sortConnections(filteredConnections, sortOption).map(
                      (conn, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-4 border-b pb-4 px-6 relative"
                        >
                          <Link to={`/user/profile/${conn.slug}`}>
                            {conn.profile ? (
                              <img
                                src={conn.profile}
                                alt={conn.name}
                                className="w-12 h-12 rounded-full object-cover"
                              />
                            ) : (
                              <div
                                className="w-12 h-12 rounded-full  text-white flex items-center justify-center font-semibold text-lg"
                                style={getColorFromName(conn.name || "?")}
                              >
                                {conn.name.charAt(0)}
                              </div>
                            )}
                          </Link>

                          <Link
                            to={`/user/profile/${conn.slug}`}
                            className="flex-1"
                          >
                            <div className="font-medium">{conn.name}</div>
                            <div className="text-sm text-gray-700">
                              {conn.headline}
                            </div>
                          </Link>

                          <div className="flex items-center gap-4">
                            <button className="text-blue-600 border border-blue-600 px-4 py-1 rounded-full text-sm hover:bg-blue-50">
                              Message
                            </button>

                            <div className="relative" ref={dropdownRef}>
                              <BsThreeDots
                                onClick={() => handleDotsClick(index)}
                                className="cursor-pointer"
                              />

                              <AnimatePresence>
                                {openDropdownIndex === index && (
                                  <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.2 }}
                                    className="absolute right-0 top-6 bg-white border shadow-md rounded-md mt-1 p-2 w-56 z-50"
                                  >
                                    <button
                                      onClick={() =>
                                        handleConfirmDelete(
                                          conn.request_id,
                                          conn.name
                                        )
                                      }
                                      className="flex items-center gap-1 text-xs text-gray-700 w-full text-left p-1 hover:bg-gray-100"
                                    >
                                      <DeleteIcon className="w-4 h-4 text-gray-700" />
                                      Remove connection
                                    </button>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          </div>
                        </div>
                      )
                    )
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No connections found matching your search.
                    </div>
                  )}

                  {confirmDelete.show && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                      <div className="bg-white p-6 rounded-lg shadow-xl w-96">
                        <h2 className="text-lg font-semibold mb-4">
                          Remove Connection
                        </h2>
                        <p className="text-sm text-gray-700 mb-6">
                          Are you sure you want to remove{" "}
                          <strong>{confirmDelete.name}</strong> from your
                          connections?
                        </p>
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() =>
                              setConfirmDelete({
                                show: false,
                                id: null,
                                name: "",
                              })
                            }
                            className="px-4 py-1 border border-gray-400 text-gray-700 rounded-md hover:bg-gray-100"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleDelete(confirmDelete.id)}
                            className="px-4 py-1 bg-red-600 text-white rounded-md hover:bg-red-700"
                          >
                            Yes, Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Pagination controls */}
                {meta && meta.totalPages > 1 && (
                  <div className="flex justify-between items-center px-4 py-3 border-t">
                    <button
                      onClick={handlePrev}
                      disabled={page === 1}
                      className={`px-4 py-1 rounded-md ${
                        page === 1
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-blue-600 hover:bg-blue-50"
                      }`}
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {page} of {meta.totalPages}
                    </span>
                    <button
                      onClick={handleNext}
                      disabled={page === meta.totalPages}
                      className={`px-4 py-1 rounded-md ${
                        page === meta.totalPages
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-blue-600 hover:bg-blue-50"
                      }`}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="w-full lg:w-1/4 "></div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConnectionsList;








